import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import {
  formatIsoDateTimeStringToDisplayDate,
  secondsToHms
} from 'common/utils/LEGACY_timeHelpers';
import Blur from 'features/blur/blur';
import { NavLink } from 'react-router-dom';
import { PaginationTableRowSelectionCheckbox } from 'features/pagination/paginatedTableRowSelectionCheckbox';
import { PaginationTableSelectAllRowsCheckbox } from 'features/pagination/paginatedTableSelectAllRowsCheckbox';
import { BaseSelectedRow } from 'features/row-selection/rowSelectionSlice';
import { CurrencyDisplayWrapper } from 'features/currency/currencyDisplayWrapper';
import { StickyOptions } from 'common/components/table/tableTypes';
import MarketingSourceLogo from 'common/components/icons/marketingSourceLogo';
import FieldsRenderer from 'features/conversions/forms/views/shared/FieldsRenderer';
import { FilterNotApplicable } from 'common/components/table/cell-formatters/filterNotApplicatable';
import MapFieldRenderer from 'common/components/table/mapFieldRenderer';
import { GetOpportunitiesResponse } from '../types/get-opportunities-response';
import { getConversionTypeFriendlyName } from 'common/helpers/conversion-type.helper';
import { createLabelsCell } from './opportunity-table-helpers';
import { convertArrayToObject } from 'common/utils/array-helpers';

const detailedColumnHelper = createColumnHelper<GetOpportunitiesResponse>();

export const opportunitiesDetailedColumnDefs = (
  componentId: string,
  data: GetOpportunitiesResponse[],
  currencyCode: string,
  showOpportunityId: boolean
) => {
  const expandedFieldsColumnDefs = [
    detailedColumnHelper.accessor((row) => row.expandedFields?.type, {
      id: 'expandedFields.type',
      header: () => <span>Type</span>,
      enableColumnFilter: false,
      enableSorting: false,
      cell: (info) => info.getValue()
    }),
    detailedColumnHelper.accessor((row) => row.expandedFields?.source, {
      id: 'expandedFields.source',
      header: () => <span>Source</span>,
      enableColumnFilter: false,
      enableSorting: false,
      cell: (info) => info.getValue()
    })
  ];

  if (data?.some((response) => response.expandedFields?.customDataFields)) {
    expandedFieldsColumnDefs.push(
      ...data.reduce<ColumnDef<GetOpportunitiesResponse, string>[]>(
        (acc, nextResponse, index) => {
          const customDataFields =
            nextResponse.expandedFields?.customDataFields;
          Object.entries(customDataFields ?? {}).forEach(([, value]) => {
            const { key } = value;

            if (acc.some((columnDef) => columnDef.id?.includes(key))) {
              return acc;
            }

            const newColDef = detailedColumnHelper.accessor(
              (row) => {
                const [, foundRow] =
                  Object.entries(
                    row.expandedFields?.customDataFields ?? {}
                  ).find((field) => field[1].key === key) ?? [];
                return foundRow?.value;
              },
              {
                id: key,
                header: () => <span>{key}</span>,
                enableColumnFilter: false,
                enableSorting: false,
                cell: (info) => (
                  <span className="break-all">{info.getValue()}</span>
                )
              }
            );

            acc.push(newColDef);
          });
          return acc;
        },
        []
      )
    );
  }

  const idColumn = showOpportunityId
    ? [
        detailedColumnHelper.accessor((row) => row.id, {
          id: 'id',
          header: () => <span>Opportunity ID</span>,
          enableColumnFilter: false,
          cell: (info) => <span className="font-bold">{info.getValue()}</span>
        }) as ColumnDef<GetOpportunitiesResponse, unknown>
      ]
    : [];

  return [
    ...idColumn,
    detailedColumnHelper.accessor((row) => row.id, {
      id: 'select',
      enableSorting: false,
      enableColumnFilter: false,
      header: (info) => {
        const tableRows = info.table
          .getRowModel()
          .rows.map<BaseSelectedRow>((row) => ({
            rowId: row.getValue('select'),
            rowData: row.getValue('opportunityDateTime')
          }));

        return (
          <PaginationTableSelectAllRowsCheckbox
            componentId={componentId}
            tableRows={tableRows}
          />
        );
      },
      cell: ({ row }) => (
        <PaginationTableRowSelectionCheckbox
          componentId={componentId}
          rowId={row.getValue('select')}
          rowData={row.getValue('opportunityDateTime')}
        />
      ),
      size: 50
    }),
    detailedColumnHelper.accessor((row) => row.opportunityDateTime, {
      id: 'opportunityDateTime',
      header: () => <span>Date/Time</span>,
      enableColumnFilter: false,
      size: 120,
      cell: (info) => (
        <span className="font-bold">
          {formatIsoDateTimeStringToDisplayDate(info.getValue())}
        </span>
      )
    }),
    detailedColumnHelper.accessor((row) => row.value, {
      id: 'opportunityValue',
      header: () => <span>Value</span>,
      enableColumnFilter: false,
      size: 100,
      cell: (info) => (
        <CurrencyDisplayWrapper currencyCode={currencyCode}>
          {Intl.NumberFormat('en-GB', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(info.getValue() ? info.getValue() : 0)}
        </CurrencyDisplayWrapper>
      )
    }),
    detailedColumnHelper.accessor((row) => row.visitorIdentifierOptions, {
      id: 'visitorIdentifiersOptions',
      header: () => <span>Customer</span>,
      enableColumnFilter: false,
      enableSorting: false,
      size: 250,
      cell: (info) =>
        MapFieldRenderer({
          fieldsMap: info.getValue(),
          contextKey: 'payloadFields'
        })
    }),
    detailedColumnHelper.accessor((row) => row.reference, {
      id: 'reference',
      header: () => <span>Reference</span>,
      enableColumnFilter: false,
      cell: (info) => info.getValue()
    }),
    detailedColumnHelper.accessor((row) => row.pipelineStage, {
      id: 'pipelineStage',
      header: () => <span>Stage</span>,
      enableColumnFilter: false,
      enableSorting: false,
      cell: (info) => info.getValue()
    }),
    detailedColumnHelper.accessor((row) => row.labels, {
      id: 'labels',
      header: () => <span>Labels</span>,
      enableColumnFilter: false,
      enableSorting: false,
      cell: (info) => createLabelsCell(info.row.original)
    }),
    detailedColumnHelper.accessor((row) => row.additionalDataProperties, {
      id: 'additionalDataProperties',
      header: () => <span>Additional Data</span>,
      enableColumnFilter: false,
      enableSorting: false,
      cell: (info) => {
        const additionalDataProperties = info.getValue() as {
          name: string;
          value: string;
        }[];

        const fieldsMap = convertArrayToObject(additionalDataProperties);

        return MapFieldRenderer({
          fieldsMap,
          contextKey: 'additionalDataProperties'
        });
      }
    }),
    detailedColumnHelper.accessor((row) => row.conversionType, {
      id: 'conversionType',
      header: () => <span>Conversion Type</span>,
      enableColumnFilter: false,
      enableSorting: false,
      cell: (info) => getConversionTypeFriendlyName(info.getValue())
    }),
    detailedColumnHelper.accessor((row) => row.captureDateTime, {
      id: 'captureDateTime',
      header: () => <span>Conversion Date</span>,
      enableColumnFilter: false,
      enableSorting: false,
      cell: (info) => (
        <span className="font-bold">
          {info.getValue() ? (
            formatIsoDateTimeStringToDisplayDate(info.getValue() as string)
          ) : (
            <></>
          )}
        </span>
      )
    }),
    detailedColumnHelper.accessor((row) => row.sources, {
      id: 'sources',
      header: () => <span>Sources</span>,
      enableColumnFilter: false,
      enableSorting: false,
      size: 400,
      cell: (info) =>
        info.getValue().map((value, index) => (
          <div key={index} className="flex items-center mr-2">
            <MarketingSourceLogo
              marketingSource={value}
              iconClasses="text-lg mr-1 flex"
              imageClasses="w-4 h-4 mr-1"
            />
            <span>{value}</span>
          </div>
        ))
    }),
    detailedColumnHelper.accessor((row) => row.conversionPayloadFields, {
      id: 'payloadFields',
      header: () => 'Fields',
      enableColumnFilter: false,
      enableSorting: false,
      size: 500,
      cell: (info) =>
        FieldsRenderer({
          fieldsMap: info.getValue(),
          contextKey: 'payloadFields'
        })
    }),
    detailedColumnHelper.accessor((row) => row.webPageViewCount, {
      id: 'webPageViewCount',
      header: () => <span>Page Views</span>,
      enableColumnFilter: false,
      size: 100,
      enableSorting: false,
      cell: (info) => info.getValue()
    }),
    detailedColumnHelper.accessor((row) => row.sessions, {
      id: 'sessions',
      header: () => <span>Sessions</span>,
      enableColumnFilter: false,
      enableSorting: false,
      cell: (info) => info.getValue()
    }),
    detailedColumnHelper.accessor(
      (row) => row.averageWebTouchpointDurationInSeconds,
      {
        id: 'averageWebTouchpointDurationInSeconds',
        header: () => <span>Duration</span>,
        enableColumnFilter: false,
        size: 220,
        enableSorting: false,
        cell: (info) =>
          info.getValue() ? secondsToHms(info.getValue()!) : <></>
      }
    ),
    detailedColumnHelper.group({
      header: 'First Click',
      columns: [
        detailedColumnHelper.accessor((row) => row.firstClick?.source, {
          id: 'firstClick.source',
          header: () => <span>Source</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        detailedColumnHelper.accessor((row) => row.firstClick?.group, {
          id: 'firstClick.group',
          header: () => <span>Group</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => FilterNotApplicable(info.getValue())
        }),
        detailedColumnHelper.accessor((row) => row.firstClick?.keywords, {
          id: 'firstClick.keywords',
          header: () => <span>Keywords</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        detailedColumnHelper.accessor((row) => row.firstClick?.landingPage, {
          id: 'firstClick.landingPage',
          header: () => <span>Landing Page</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        detailedColumnHelper.accessor((row) => row.firstClick?.referrerHost, {
          id: 'firstClick.referrerHost',
          header: () => <span>Referrer Host</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        detailedColumnHelper.accessor((row) => row.firstClick?.referrerPath, {
          id: 'firstClick.referrerPath',
          header: () => <span>Referrer Path</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        detailedColumnHelper.accessor((row) => row.firstClick?.canonicalUrl, {
          id: 'firstClick.canonicalUrl',
          header: () => <span>Canonical Url</span>,
          enableColumnFilter: false,
          enableSorting: false,
          size: 600,
          cell: (info) => <span className="break-all">{info.getValue()}</span>
        }),
        detailedColumnHelper.accessor((row) => row.firstClick?.utmCampaign, {
          id: 'firstClick.utmCampaign',
          header: () => <span>Utm Campaign</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur>{info.getValue()}</Blur>
        }),
        detailedColumnHelper.accessor((row) => row.firstClick?.utmContent, {
          id: 'firstClick.utmContent',
          header: () => <span>Utm Content</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur>{info.getValue()}</Blur>
        }),
        detailedColumnHelper.accessor((row) => row.firstClick?.utmMedium, {
          id: 'firstClick.utmMedium',
          header: () => <span>Utm Medium</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur>{info.getValue()}</Blur>
        }),
        detailedColumnHelper.accessor((row) => row.firstClick?.utmSource, {
          id: 'firstClick.utmSource',
          header: () => <span>Utm Source</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur>{info.getValue()}</Blur>
        }),
        detailedColumnHelper.accessor((row) => row.firstClick?.utmTerm, {
          id: 'firstClick.utmTerm',
          header: () => <span>Utm Term</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur>{info.getValue()}</Blur>
        }),
        detailedColumnHelper.accessor((row) => row.firstClick?.utmKeyword, {
          id: 'firstClick.utmKeyword',
          header: () => <span>Utm Keyword</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur>{info.getValue()}</Blur>
        }),
        detailedColumnHelper.accessor((row) => row.firstClick?.device, {
          id: 'firstClick.device',
          header: () => <span>Device</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        detailedColumnHelper.accessor((row) => row.firstClick?.placement, {
          id: 'firstClick.placement',
          header: () => <span>Placement</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        detailedColumnHelper.accessor((row) => row.firstClick?.creative, {
          id: 'firstClick.creative',
          header: () => <span>Creative</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        detailedColumnHelper.accessor((row) => row.firstClick?.campaignId, {
          id: 'firstClick.campaignId',
          header: () => <span>Campaign Id</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur> {info.getValue()}</Blur>
        }),
        detailedColumnHelper.accessor((row) => row.firstClick?.adGroupId, {
          id: 'firstClick.adGroupId',
          header: () => <span>Adgroup Id</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur> {info.getValue()}</Blur>
        }),
        detailedColumnHelper.accessor((row) => row.firstClick?.gclid, {
          id: 'firstClick.gclid',
          header: () => <span>Gclid</span>,
          enableColumnFilter: false,
          enableSorting: false,
          size: 600,
          cell: (info) => (
            <Blur>
              <span className="break-all">{info.getValue()}</span>
            </Blur>
          )
        })
      ]
    }),
    detailedColumnHelper.group({
      header: 'Last Click',
      columns: [
        detailedColumnHelper.accessor((row) => row.lastClick?.source, {
          id: 'lastClick.source',
          header: () => <span>Source</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        detailedColumnHelper.accessor((row) => row.lastClick?.group, {
          id: 'lastClick.group',
          header: () => <span>Group</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => FilterNotApplicable(info.getValue())
        }),
        detailedColumnHelper.accessor((row) => row.lastClick?.keywords, {
          id: 'lastClick.keywords',
          header: () => <span>Keywords</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        detailedColumnHelper.accessor((row) => row.lastClick?.landingPage, {
          id: 'lastClick.landingPage',
          header: () => <span>Landing Page</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        detailedColumnHelper.accessor((row) => row.lastClick?.referrerHost, {
          id: 'lastClick.referrerHost',
          header: () => <span>Referrer Host</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        detailedColumnHelper.accessor((row) => row.lastClick?.referrerPath, {
          id: 'lastClick.referrerPath',
          header: () => <span>Referrer Path</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        detailedColumnHelper.accessor((row) => row.lastClick?.canonicalUrl, {
          id: 'lastClick.canonicalUrl',
          header: () => <span>Canonical Url</span>,
          enableColumnFilter: false,
          enableSorting: false,
          size: 600,
          cell: (info) => <span className="break-all">{info.getValue()}</span>
        }),
        detailedColumnHelper.accessor((row) => row.lastClick?.utmCampaign, {
          id: 'lastClick.utmCampaign',
          header: () => <span>Utm Campaign</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur> {info.getValue()}</Blur>
        }),
        detailedColumnHelper.accessor((row) => row.lastClick?.utmContent, {
          id: 'lastClick.utmContent',
          header: () => <span>Utm Content</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur> {info.getValue()}</Blur>
        }),
        detailedColumnHelper.accessor((row) => row.lastClick?.utmMedium, {
          id: 'lastClick.utmMedium',
          header: () => <span>Utm Medium</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur> {info.getValue()}</Blur>
        }),
        detailedColumnHelper.accessor((row) => row.lastClick?.utmSource, {
          id: 'lastClick.utmSource',
          header: () => <span>Utm Source</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        detailedColumnHelper.accessor((row) => row.lastClick?.utmTerm, {
          id: 'lastClick.utmTerm',
          header: () => <span>Utm Term</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur> {info.getValue()}</Blur>
        }),
        detailedColumnHelper.accessor((row) => row.lastClick?.utmKeyword, {
          id: 'lastClick.utmKeyword',
          header: () => <span>Utm Keyword</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur> {info.getValue()}</Blur>
        }),
        detailedColumnHelper.accessor((row) => row.lastClick?.device, {
          id: 'lastClick.device',
          header: () => <span>Device</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        detailedColumnHelper.accessor((row) => row.lastClick?.placement, {
          id: 'lastClick.placement',
          header: () => <span>Placement</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        detailedColumnHelper.accessor((row) => row.lastClick?.creative, {
          id: 'lastClick.creative',
          header: () => <span>Creative</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        detailedColumnHelper.accessor((row) => row.lastClick?.campaignId, {
          id: 'lastClick.campaignId',
          header: () => <span>Campaign Id</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur> {info.getValue()}</Blur>
        }),
        detailedColumnHelper.accessor((row) => row.lastClick?.adGroupId, {
          id: 'lastClick.adGroupId',
          header: () => <span>Adgroup Id</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur> {info.getValue()}</Blur>
        }),
        detailedColumnHelper.accessor((row) => row.lastClick?.gclid, {
          id: 'lastClick.gclid',
          header: () => <span>Gclid</span>,
          enableColumnFilter: false,
          enableSorting: false,
          size: 600,
          cell: (info) => (
            <Blur>
              <span className="break-all">{info.getValue()}</span>
            </Blur>
          )
        })
      ]
    }),
    detailedColumnHelper.group({
      header: 'Device And Location',
      columns: [
        detailedColumnHelper.accessor(
          (row) => row.deviceAndLocation?.platform,
          {
            id: 'deviceAndLocation.platform',
            header: () => <span>Platform</span>,
            enableColumnFilter: false,
            enableSorting: false,
            cell: (info) => info.getValue()
          }
        ),
        detailedColumnHelper.accessor((row) => row.deviceAndLocation?.browser, {
          id: 'deviceAndLocation.browser',
          header: () => <span>Browser</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        detailedColumnHelper.accessor(
          (row) => row.deviceAndLocation?.operatingSystem,
          {
            id: 'deviceAndLocation.operatingSystem',
            header: () => <span>OS</span>,
            enableColumnFilter: false,
            enableSorting: false,
            cell: (info) => info.getValue()
          }
        )
      ]
    }),
    detailedColumnHelper.group({
      header: 'Identity',
      columns: [
        detailedColumnHelper.accessor((row) => row.identity?.email, {
          id: 'identity.email',
          header: () => <span>Email</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        })
      ]
    }),
    detailedColumnHelper.group({
      header: 'Expanded Fields',
      columns: expandedFieldsColumnDefs
    }),
    detailedColumnHelper.accessor((row) => row.visitorId, {
      id: 'Visitor',
      header: () => <span>Visitor</span>,
      enableColumnFilter: false,
      enableSorting: false,
      cell: (info) => {
        const visitorId = info.getValue();

        if (!visitorId) {
          return <></>;
        }

        return (
          <NavLink
            title="View"
            to={`/visitors/${visitorId}`}
            className="bg-primary-700 hover:bg-primary-600 focus:bg-primary-800 active:bg-primary-800 border-primary-700 hover:border-primary-600 focus:border-primary-800 active:border-primary-800 px-5 py-2.5 transition-all text-white rounded border text-sm text-center"
          >
            View
          </NavLink>
        );
      },
      meta: {
        stickyOptions: {
          isEnabled: true,
          side: 'right'
        } as StickyOptions
      },
      enableResizing: false,
      size: 100
    })
  ];
};

export default opportunitiesDetailedColumnDefs;
